import {
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import { Button } from "components/UI";
import { useChurchStore } from "features/churches";
import {
  useCreateDefaultChartOfAccounts,
  useDefaultChartOfAccounts,
  useFinanceSettingsStore
} from "features/finances";
import { useEffect, useMemo, useRef, useState } from "react";

export const DefaultChartOfAccountsModal = ({ setActiveTab }) => {
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);
  const errorRef = useRef(null);

  const defaultChartOfAccountsOpen = useFinanceSettingsStore(
    state => state.defaultChartOfAccountsOpen
  );
  const closeDefaultChartOfAccountsModal = useFinanceSettingsStore(
    state => state.closeDefaultChartOfAccountsModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const createDefaultChartOfAccountsMutation =
    useCreateDefaultChartOfAccounts();

  const { data: defaultChartOfAccountsList } = useDefaultChartOfAccounts();

  const handleChartOfAccountChange = event => {
    if (event.target.checked) {
      setSelectedAccounts([...selectedAccounts, event.target.name]);
    } else {
      setSelectedAccounts(
        selectedAccounts.filter(account => account !== event.target.name)
      );
    }
  };

  const handleSelectAll = event => {
    if (event.target.checked) {
      setSelectedAccounts(
        defaultChartOfAccountsList.map(account => account.account_name)
      );
    } else {
      setSelectedAccounts([]);
    }
  };

  const generateDefaultChartOfAccounts = () => {
    setErrorMessages([]);

    createDefaultChartOfAccountsMutation.mutate(
      {
        churchId: selectedChurch?.id,
        accountData: {
          account_names: selectedAccounts
        }
      },
      {
        onSuccess: () => {
          setSelectedAccounts([]);
          setActiveTab("chartOfAccount");
        },
        onError: response => {
          const serverErrors = Object.values(response.errors);
          const serverErrorMessages = [];

          for (const serverError of serverErrors) {
            serverErrorMessages.push(serverError[0]);
          }

          setErrorMessages(serverErrorMessages);
        }
      }
    );
  };

  useEffect(() => {
    if (errorMessages.length !== 0) {
      errorRef.current.scrollIntoView(false);
    }
  }, [errorMessages]);

  const groupedAccounts = useMemo(() => {
    if (!defaultChartOfAccountsList) return [];

    return defaultChartOfAccountsList?.reduce((acc, account) => {
      if (!acc[account.account_type]) {
        acc[account.account_type] = [];
      }
      acc[account.account_type].push(account);
      return acc;
    }, {});
  }, [defaultChartOfAccountsList]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={defaultChartOfAccountsOpen}
      onClose={closeDefaultChartOfAccountsModal}
    >
      <DialogTitle>Default Chart Of Accounts</DialogTitle>
      <DialogContent>
        <FormGroup sx={{ gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectedAccounts.length === defaultChartOfAccountsList?.length
                }
                onChange={handleSelectAll}
              />
            }
            label="All"
          />
          {Object.entries(groupedAccounts).map(([accountType, accounts]) => (
            <div key={accountType}>
              <Divider
                textAlign="left"
                sx={{ textTransform: "capitalize", mb: 2, mt: 1 }}
              >
                <Chip label={accountType} variant="outlined" />
              </Divider>
              <Grid container spacing={2}>
                {accounts.map(account => (
                  <Grid item xs={6} key={account.account_name}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAccounts.includes(
                            account.account_name
                          )}
                        />
                      }
                      label={
                        <Stack spacing={1}>
                          <Typography>{account.account_name}</Typography>
                          <Typography variant="caption" color="gray">
                            {account.account_number}
                          </Typography>
                        </Stack>
                      }
                      name={account.account_name}
                      onChange={handleChartOfAccountChange}
                    />
                  </Grid>
                ))}
              </Grid>
            </div>
          ))}
        </FormGroup>
        {errorMessages.length !== 0 &&
          errorMessages.map(errorMessage => (
            <FormHelperText key={errorMessage} error ref={errorRef}>
              {errorMessage}
            </FormHelperText>
          ))}
      </DialogContent>
      <DialogActions>
        <Button
          flavor="outlined"
          onClick={closeDefaultChartOfAccountsModal}
          disabled={createDefaultChartOfAccountsMutation.isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={generateDefaultChartOfAccounts}
          disabled={
            selectedAccounts.length === 0 ||
            createDefaultChartOfAccountsMutation.isLoading
          }
        >
          Generate
        </Button>
      </DialogActions>
    </Dialog>
  );
};
